import { Link } from "wouter";
import { MapPin, Heart } from "lucide-react";
import { useRestyleVisibility, useShopOwnerFeatures } from "@/lib/featureFlags";
import { getLocalizedPath, LOCALE_LABELS, persistPreferredLocale, useI18n, withLocalePrefix } from "@/lib/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { AppLocale } from "@shared/i18n-locales";

export function Footer() {
  const { canSeeRestyle } = useRestyleVisibility();
  const { canSeeShopOwner } = useShopOwnerFeatures();
  const { locale, t } = useI18n();

  const onLocaleChange = (nextLocale: string) => {
    const safeLocale = (nextLocale || "en") as AppLocale;
    if (safeLocale === locale) return;
    persistPreferredLocale(safeLocale);
    const targetPath = withLocalePrefix(window.location.pathname || "/", safeLocale);
    window.location.assign(`${targetPath}${window.location.search || ""}${window.location.hash || ""}`);
  };

  return (
    <footer className="border-t border-border bg-card mt-auto">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <MapPin className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="font-semibold text-lg">{t("app.name")}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="font-medium mb-4">{t("footer.discover")}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link href={getLocalizedPath("routes", locale)} className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("nav.routes")}</Link></li>
              <li><Link href={getLocalizedPath("shops", locale)} className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("shops.title")}</Link></li>
              <li><Link href={getLocalizedPath("community", locale)} className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("nav.community")}</Link></li>
              {canSeeRestyle && (
                <li><Link href={getLocalizedPath("restyle", locale)} className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("nav.restyle")}</Link></li>
              )}
            </ul>
          </div>

          {canSeeShopOwner && (
            <div>
              <h4 className="font-medium mb-4">{t("footer.forShops")}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link href="/owners/kringloop" className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("footer.claimShop")}</Link></li>
              </ul>
            </div>
          )}
          <div>
            <h4 className="font-medium mb-4">{t("footer.aboutUs")}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link href="/over-vintmap" className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("footer.aboutVintmap")}</Link></li>
              <li><Link href="/faq" className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("footer.faq")}</Link></li>
              <li><Link href="/privacy" className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("footer.privacy")}</Link></li>
              <li><Link href="/voorwaarden" className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("footer.terms")}</Link></li>
              <li><Link href="/disclaimer" className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("footer.disclaimer")}</Link></li>
              <li><Link href="/contact" className="hover:text-foreground transition-colors" onClick={() => window.scrollTo(0, 0)}>{t("footer.contact")}</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border mt-8 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>{t("footer.copyright")}</p>
          <div className="flex items-center gap-3">
            <span>{t("footer.language")}</span>
            <Select value={locale} onValueChange={onLocaleChange}>
              <SelectTrigger className="w-[170px] h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(LOCALE_LABELS).map(([code, label]) => (
                  <SelectItem key={code} value={code}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="flex items-center gap-1">
            {t("footer.madeWith")} <Heart className="h-3 w-3 text-primary fill-primary" /> {t("footer.sustainability")}
          </p>
        </div>
      </div>
    </footer>
  );
}
